import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import '../styles/AdminUsuarios.css';

const ROLES = ['admin', 'editor'];

const ADMIN_VACIO = { nombre: '', email: '', password: '', rol: 'admin' };

export default function AdminUsuarios({ isOpen, onClose }) {
  const { usuario } = useContext(AuthContext);
  const [admins, setAdmins] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(null);   // { _id, nombre, email, rol }
  const [creando, setCreando] = useState(false);
  const [form, setForm] = useState(ADMIN_VACIO);
  const [passwordNueva, setPasswordNueva] = useState('');
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    cargar();
  }, [isOpen]);

  const cargar = () => {
    setCargando(true);
    setError('');
    fetch('/api/admins')
      .then(r => r.json())
      .then(data => setAdmins(Array.isArray(data) ? data : []))
      .catch(() => setError('Error al cargar administradores'))
      .finally(() => setCargando(false));
  };

  const mostrarExito = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(''), 3000);
  };

  // ── CREAR ──
  const abrirCrear = () => {
    setForm(ADMIN_VACIO);
    setError('');
    setCreando(true);
    setEditando(null);
  };

  const guardarNuevo = async () => {
    if (!form.nombre.trim() || !form.email.trim() || !form.password) {
      setError('Nombre, email y contraseña son requeridos');
      return;
    }
    setGuardando(true);
    setError('');
    try {
      const res = await fetch('/api/admins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al crear admin'); return; }
      setAdmins(prev => [...prev, data.admin]);
      setCreando(false);
      mostrarExito('Admin creado exitosamente');
    } catch { setError('Error de red'); }
    finally { setGuardando(false); }
  };

  // ── EDITAR ──
  const abrirEditar = (admin) => {
    setEditando(admin);
    setForm({ nombre: admin.nombre, email: admin.email, rol: admin.rol || 'admin' });
    setPasswordNueva('');
    setError('');
    setCreando(false);
  };

  const guardarEdicion = async () => {
    if (!form.nombre.trim() || !form.email.trim()) {
      setError('Nombre y email son requeridos');
      return;
    }
    setGuardando(true);
    setError('');
    const payload = { nombre: form.nombre, email: form.email, rol: form.rol };
    if (passwordNueva) payload.password = passwordNueva;
    try {
      const res = await fetch(`/api/admins/${editando._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al actualizar'); return; }
      setAdmins(prev => prev.map(a => a._id === editando._id ? data.admin : a));
      setEditando(null);
      mostrarExito('Admin actualizado exitosamente');
    } catch { setError('Error de red'); }
    finally { setGuardando(false); }
  };

  // ── ELIMINAR ──
  const eliminar = async (id) => {
    setGuardando(true);
    try {
      const res = await fetch(`/api/admins/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al eliminar'); setConfirmEliminar(null); return; }
      setAdmins(prev => prev.filter(a => a._id !== id));
      setConfirmEliminar(null);
      mostrarExito('Admin eliminado');
    } catch { setError('Error de red'); }
    finally { setGuardando(false); }
  };

  const cancelar = () => {
    setCreando(false);
    setEditando(null);
    setError('');
    setPasswordNueva('');
  };

  if (!isOpen) return null;

  const formularioAbierto = creando || editando;

  return (
    <div className="au-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="au-modal">

        {/* Header */}
        <div className="au-header">
          <h2>Administradores</h2>
          <button className="au-btn-cerrar" onClick={onClose}>✕</button>
        </div>

        {/* Mensajes */}
        {error  && <p className="au-error">{error}</p>}
        {exito  && <p className="au-exito">{exito}</p>}

        {/* Formulario crear / editar */}
        {formularioAbierto && (
          <div className="au-form">
            <h3>{creando ? 'Nuevo administrador' : 'Editar administrador'}</h3>

            <div className="au-form-fila">
              <div className="au-campo">
                <label>Nombre</label>
                <input
                  className="au-input"
                  value={form.nombre}
                  onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Nombre completo"
                />
              </div>
              <div className="au-campo">
                <label>Email</label>
                <input
                  className="au-input"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>

            <div className="au-form-fila">
              <div className="au-campo">
                <label>{creando ? 'Contraseña' : 'Nueva contraseña (dejar vacío para no cambiar)'}</label>
                <input
                  className="au-input"
                  type="password"
                  value={creando ? form.password : passwordNueva}
                  onChange={e =>
                    creando
                      ? setForm(p => ({ ...p, password: e.target.value }))
                      : setPasswordNueva(e.target.value)
                  }
                  placeholder="Mín. 6 caracteres"
                />
              </div>
              <div className="au-campo">
                <label>Rol</label>
                <select
                  className="au-select"
                  value={form.rol}
                  onChange={e => setForm(p => ({ ...p, rol: e.target.value }))}
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="au-form-acciones">
              <button className="au-btn-cancelar" onClick={cancelar} disabled={guardando}>
                Cancelar
              </button>
              <button
                className="au-btn-guardar"
                onClick={creando ? guardarNuevo : guardarEdicion}
                disabled={guardando}
              >
                {guardando ? 'Guardando...' : creando ? 'Crear admin' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        )}

        {/* Lista */}
        <div className="au-lista">
          {cargando ? (
            <p className="au-mensaje">Cargando...</p>
          ) : admins.length === 0 ? (
            <p className="au-mensaje">No hay administradores registrados.</p>
          ) : (
            admins.map(admin => (
              <div key={admin._id} className={`au-item ${!admin.activo ? 'inactivo' : ''}`}>
                <div className="au-item-info">
                  <span className="au-item-nombre">
                    {admin.nombre}
                    {admin.email === usuario?.email && (
                      <span className="au-tag-yo"> (vos)</span>
                    )}
                  </span>
                  <span className="au-item-email">{admin.email}</span>
                  <span className={`au-tag-rol rol-${admin.rol}`}>{admin.rol}</span>
                  {!admin.activo && <span className="au-tag-inactivo">inactivo</span>}
                </div>
                <div className="au-item-acciones">
                  <button
                    className="au-btn-editar"
                    onClick={() => abrirEditar(admin)}
                    title="Editar"
                  >
                    ✎
                  </button>
                  {admin.email !== usuario?.email && (
                    <button
                      className="au-btn-eliminar"
                      onClick={() => setConfirmEliminar(admin)}
                      title="Eliminar"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Botón agregar */}
        {!formularioAbierto && (
          <div className="au-footer">
            <button className="au-btn-nuevo" onClick={abrirCrear}>
              ➕ Agregar administrador
            </button>
          </div>
        )}

        {/* Confirmación eliminar */}
        {confirmEliminar && (
          <div className="au-confirm-overlay" onClick={() => setConfirmEliminar(null)}>
            <div className="au-confirm" onClick={e => e.stopPropagation()}>
              <p>¿Eliminar a <strong>{confirmEliminar.nombre}</strong>?</p>
              <p className="au-confirm-sub">Esta acción no se puede deshacer.</p>
              <div className="au-confirm-acciones">
                <button className="au-btn-cancelar" onClick={() => setConfirmEliminar(null)}>
                  Cancelar
                </button>
                <button
                  className="au-btn-eliminar-confirm"
                  onClick={() => eliminar(confirmEliminar._id)}
                  disabled={guardando}
                >
                  {guardando ? 'Eliminando...' : 'Sí, eliminar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
